import jwt_decode from "jwt-decode";
import apiClient from "./apiClient";

interface DecodedToken {
  name: string;
  role: string;
  exp: number;
  iat: number;
}

// Sign in and store both access and refresh tokens
export const signIn = async (email: string, password: string) => {
  try {
    const response = await apiClient.post("/signin", { email, password });

    const accessToken = response.data.token;
    const refreshToken = "";

    localStorage.setItem(
      "tokens",
      JSON.stringify({ accessToken, refreshToken })
    );

    return response.data;
  } catch (error) {
    throw new Error("Authentication failed. Please check your credentials.");
  }
};

// Decode the stored access token
export const getCurrentUser = (): { name: string; role: string } | null => {
  try {
    const tokenString = localStorage.getItem("tokens");

    if (!tokenString) throw new Error("Token storage missing");

    const { accessToken } = JSON.parse(tokenString);

    if (!accessToken || accessToken.split(".").length !== 3) {
      throw new Error("Malformed or missing access token");
    }

    const decoded = jwt_decode<DecodedToken>(accessToken);
    return {
      name: decoded.name,
      role: decoded.role,
    };
  } catch (e) {
    console.error("Invalid token", e);
    localStorage.removeItem("tokens");
    return null;
  }
};

export const isAuthenticated = (): boolean => {
  const tokens = localStorage.getItem("tokens");
  return !!tokens;
};

export const logout = () => {
  localStorage.removeItem("tokens");
  sessionStorage.removeItem("tokens");
  window.location.href = "/auth";
};

// The following requests no longer need manual auth headers
export const sendPasswordResetEmail = async (email: string) => {
  const response = await apiClient.post("/auth/forgot-password", { email });
  return response.data;
};

export const requestPasswordReset = async (email: string): Promise<void> => {
  try {
    const response = await apiClient.post("/request-password-reset", { email });
    return response.data;
  } catch (error: any) {
    const message =
      error.response?.data?.message || "Failed to request password reset.";
    throw new Error(message);
  }
};

export const verifyResetOtp = async (
  email: string,
  otp: number
): Promise<void> => {
  try {
    const response = await apiClient.post("/verify-otp", { email, otp });
    return response.data;
  } catch (error: any) {
    const message = error.response?.data?.message || "Invalid reset OTP.";
    throw new Error(message);
  }
};

export const resetPassword = async (email: string, password: string) => {
  try {
    const response = await apiClient.post("/reset-password", {
      email,
      password,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Password reset failed.");
  }
};
